"use client";

import classNames from "@/helpers/classNames";
import Icon from "@/components/Icon";
import Text from "@/components/Text";
import Tooltip from "@/components/Tooltip";
import type {
  IProductOption as ProductOption,
  IProductOptionValue as OptionValue,
} from "@/tools/sanity/schema/documents/product";
import styles from "./styles.module.scss";

export interface ProductOptionsProps {
  options: ProductOption[];
  selections: Record<string, string>;
  onSelect: (optionTitle: string, valueTitle: string) => void;
  className?: string;
}

const ProductOptions = ({ options, selections, onSelect, className }: ProductOptionsProps) => {
  if (!options || options.length === 0) {
    return null;
  }

  return (
    <div className={classNames(styles.container, className)}>
      {options.map((option) => {
        const displayTitle = option.customTitle || option.title;
        const selectedValue = selections[option.title];

        return (
          <div key={option._id} className={styles.optionGroup}>
            <div className={styles.labelRow}>
              <Text
                as="span"
                text={displayTitle.toUpperCase()}
                variant="label"
                size="sm"
                weight="medium"
                className={styles.label}
              />
              {option.note && (
                <Tooltip text={option.note}>
                  <Icon title="questionFill" size="fluid" className={styles.noteIcon} />
                </Tooltip>
              )}
            </div>

            <div className={styles.optionsGrid}>
              {(option.values ?? []).map((value) => {
                const valueDisplay = value.customTitle || value.title;
                const isSelected = selectedValue === value.title;

                return (
                  <button
                    key={value.title}
                    type="button"
                    className={classNames(styles.optionCard, {
                      [styles.optionCardSelected]: isSelected,
                    })}
                    onClick={() => onSelect(option.title, value.title)}
                    aria-pressed={isSelected}
                  >
                    <div className={styles.optionHeader}>
                      {value.icon && (
                        <div className={styles.optionIcon}>
                          <Icon
                            className={styles.optionIconSvg}
                            title={value.icon as any}
                            size="md"
                          />
                        </div>
                      )}
                      <Text
                        as="span"
                        text={valueDisplay}
                        size="sm"
                        weight="semibold"
                        className={styles.optionTitle}
                      />
                    </div>
                    {value.description && (
                      <Text
                        as="p"
                        text={value.description}
                        variant="label"
                        size="md"
                        className={styles.optionDescription}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProductOptions;
export type { ProductOption, OptionValue };
